<template>
  <div
    class="filtersContainer"
    ref="filtersContainer"
    :style="{ width: `${width}px` }"
    :class="{ 'has-filters': hasActiveFilters }"
  >
    <div class="filters" v-show="hasActiveFilters">
      <selected-filter-item
        v-for="(item, id) in columnFiltersByKey(field)"
        :id="id"
        :key="id"
        :filter-item="item"
        @deleteFilter="deleteFilter"
        @editFilter="editFilter"
      />
    </div>
    <div class="inputContainer">
      <span class="search-input" @click="focusInput">
        <ico name="icoSearch" />
      </span>
      <input
        v-if="!type"
        type="text"
        ref="filterInput"
        class="filterInput"
        v-model="currentValue"
        @keyup.enter.exact="onEnterPressed"
        :disabled="disabled"
      />
      <input
        v-else-if="type === 'date'"
        type="text"
        ref="filterInput"
        class="filterInput"
        v-model="currentValue"
        @keyup.enter.exact="onEnterPressed"
        :disabled="disabled"
        v-mask="{
          mask: '99.99.9999',
          placeholder: 'дд.мм.гггг',
          showMaskOnHover: false,
        }"
      />
      <input
        v-else-if="type === 'number'"
        type="text"
        ref="filterInput"
        class="filterInput"
        v-model="currentValue"
        @keyup.enter.exact="onEnterPressed"
        :disabled="disabled"
        v-mask="{
          mask: '999',
          placeholder: '',
          showMaskOnHover: false,
          greedy: true,
        }"
      />
      <input
        v-else-if="type === 'decimal'"
        type="text"
        ref="filterInput"
        class="filterInput"
        v-model="currentValue"
        @keyup.enter.exact="onEnterPressed"
        :disabled="disabled"
      />
      <button v-if="editableFilterId" @click="cancelEdit" type="button">
        <ico name="icoCancel" size="16" color="#79919C" />
      </button>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { uuid } from "vue-uuid";
import SelectedFilterItem from "../../common/SelectedFilterItem";
import { mapActions, mapGetters } from "vuex";
import { dateValid } from "@/utils/datetime";
import { isEmpty, values } from "@/utils/lodash";
import { purchasesPath } from "@/router/purchases";
import { eventBus } from "@/main";

export default Vue.extend({
  name: "PurchasesTreeTableFilterItem",

  components: {
    SelectedFilterItem,
  },

  props: {
    field: {
      type: String,
      required: true,
    },

    type: {
      type: String,
      default: null,
    },

    filterPath: {
      type: String,
      required: true,
    },

    operation: {
      type: Function,
      default: undefined,
    },

    width: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      currentValue: "",
      editableFilterId: null,
      isValid: false,
    };
  },

  computed: {
    ...mapGetters("columnsFilters", ["columnFiltersByKey"]),
    ...mapGetters("auth", ["authorities"]),
    ...mapGetters("filterTabs", ["currentTab"]),

    disabled() {
      return (
        !this.authorities.includes("ROLE_SUBSCRIBED_USER") &&
        values(this.columnFiltersByKey(this.field)).length > 0 &&
        !this.editableFilterId
      );
    },

    hasActiveFilters() {
      return values(this.columnFiltersByKey(this.field)).length > 0;
    },
  },

  methods: {
    ...mapActions("columnsFilters", ["addColumnFilter", "removeColumnFilter"]),
    ...mapActions("filterTabs", ["setZeroAllLots"]),

    cancelEdit() {
      this.clearState();
    },

    onEnterPressed(event) {
      switch (this.type) {
        case "number":
          this.onChangeNumberInput(event);
          break;

        case "decimal":
          this.onChangeDecimalInput(event);
          break;

        case "date":
          this.onChangeDateInput(event);
          break;

        default:
          this.onChangeInput(event);
      }

      if (this.isValid) {
        this.pushRoute();
      }
    },

    onChangeDateInput(event) {
      if (!dateValid(event.target.value)) {
        this.isValid = false;
        return;
      }

      this.isValid = true;
      this.setNewCurrentValue(event.target.value);
      this.clearState();
    },

    onChangeNumberInput(event) {
      if (event.target.value === "") {
        this.isValid = false;
        return;
      }

      this.isValid = true;
      this.setNewCurrentValue(event.target.value);
      this.clearState();
    },

    onChangeDecimalInput(event) {
      if (!/^\d+(?:\.\d{2})?$/.test(event.target.value)) {
        this.isValid = false;
        return;
      }

      this.isValid = true;
      this.setNewCurrentValue(event.target.value);
      this.clearState();
    },

    onChangeInput(event) {
      if (event.target.value === "") {
        this.isValid = false;
        return;
      }

      this.isValid = true;
      this.setNewCurrentValue(event.target.value);
      this.clearState();
    },

    setNewCurrentValue(value) {
      const id = this.editableFilterId ?? uuid.v4();

      this.addColumnFilter({
        id,
        value: {
          text: value,
        },
        field: this.field,
        filterPath: this.filterPath,
        operation: this.operation,
      });
    },

    deleteFilter(id) {
      this.removeColumnFilter({
        id,
        field: this.field,
        filterPath: this.filterPath,
        operation: this.operation,
      });

      this.clearState();
      this.pushRoute();
    },

    editFilter(id, text) {
      this.editableFilterId = id;
      this.currentValue = text;
      this.isValid = true;
    },

    clearState() {
      this.currentValue = "";
      this.editableFilterId = null;
      this.isValid = false;
    },

    pushRoute() {
      if (
        parseInt(this.$route.params.id) === this.currentTab.id &&
        isEmpty(this.$route.query)
      ) {
        this.setZeroAllLots(this.currentTab);
        return;
      }

      this.$router.push(`${purchasesPath}/${this.currentTab.id}`);
      this.setZeroAllLots(this.currentTab);
    },

    resetFilterInput() {
      this.currentValue = "";
    },

    focusInput() {
      this.$refs.filterInput?.focus();
    },
  },

  mounted() {
    eventBus.$on("resetFilterInput", this.resetFilterInput);
  },

  beforeDestroy() {
    eventBus.$off("resetFilterInput", this.resetFilterInput);
  },
});
</script>
<style lang="less" scoped>
@import "../../../assets/less/variables.less";

::-webkit-scrollbar {
  width: 2px;
  height: 2px;
}

.filtersContainer {
  padding-top: 2px;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;

  &.has-filters {
    min-height: 50px;
  }
}

.filters {
  flex-shrink: 0;
  min-height: 26px;
  height: auto;
  display: flex;
  white-space: nowrap;
  flex-wrap: wrap;
}

.filterInput {
  width: 100%;
  padding: 2px 4px;
  border-right: 0;
  border-left: 0;
  border-top: 0;
  border-bottom-color: @text-color-lighter;
  letter-spacing: 0.3px;
  background-color: transparent;
  color: #37474f;

  &:focus {
    border-bottom-color: @text-color-light;
  }
}

.filterInput:focus {
  outline: none;
}

.inputContainer {
  display: flex;

  .search-input {
    display: flex;
    cursor: pointer;
    color: #79919c;
  }
}
</style>
